import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { BrandMark } from '../components/BrandMark';

type WeddingMeta = {
  event_name: string;
  couple_names: string;
  event_date: string | null;
  event_time: string | null;
  venue_name: string | null;
  venue_address: string | null;
  waze_url: string | null;
  maps_url: string | null;
  gift_path: string;
  gift_ready: boolean;
  public_token: string;
};

function formatDateHe(date: string | null): string {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat('he-IL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(date));
  } catch {
    return date;
  }
}

export default function WeddingPublicPage() {
  const { token = '' } = useParams();
  const [meta, setMeta] = useState<WeddingMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/wedding/${token}`)
      .then((r) => alive && setMeta(r.data))
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6" role="status">
        <div className="text-center space-y-3">
          <div className="mx-auto h-10 w-10 rounded-full border-2 border-sage-200 border-t-sage-600 animate-spin" />
          <p className="text-ink-mute text-sm">טוען…</p>
        </div>
      </div>
    );
  }

  if (notFound || !meta) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6">
        <div className="text-center max-w-sm surface p-8">
          <h1 className="font-display text-2xl font-bold text-ink">העמוד לא נמצא</h1>
          <p className="text-ink-mute mt-2 text-sm">ייתכן שהקישור שגוי או שפג תוקף.</p>
        </div>
      </div>
    );
  }

  const dateLine = formatDateHe(meta.event_date);
  const timeLine = meta.event_time ? `קבלת פנים בשעה ${meta.event_time}` : null;

  return (
    <div dir="rtl" className="min-h-full">
      <div className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 55% at 50% 0%, rgba(163,106,122,0.18), transparent 70%), linear-gradient(180deg, #faf6f3 0%, #f3ece7 100%)',
          }}
        />

        <div className="relative mx-auto w-full max-w-lg px-5 pt-10 pb-16 page-enter">
          <header className="text-center space-y-5">
            <BrandMark className="mx-auto opacity-80" />
            <p className="text-[13px] tracking-[0.22em] text-ink-mute font-semibold uppercase">
              הזמנה לחתונה
            </p>
            <h1 className="font-display text-[2.65rem] sm:text-5xl font-bold text-ink leading-[1.15] tracking-tight">
              {meta.couple_names}
            </h1>
            <div className="mx-auto h-px w-16 bg-sage-400/70" />
            <div className="space-y-1.5 text-ink-mute">
              {dateLine ? <p className="text-[17px] font-medium text-ink">{dateLine}</p> : null}
              {timeLine ? <p className="text-[15px]">{timeLine}</p> : null}
            </div>
          </header>

          <section className="mt-10 surface p-6 text-center space-y-3">
            {meta.venue_name ? (
              <h2 className="font-display text-2xl font-bold text-ink">{meta.venue_name}</h2>
            ) : null}
            {meta.venue_address ? (
              <p className="text-[15px] text-ink-mute leading-relaxed">{meta.venue_address}</p>
            ) : null}

            <div className="pt-2 flex flex-col gap-2.5">
              {meta.waze_url ? (
                <a
                  href={meta.waze_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full inline-flex items-center justify-center"
                >
                  ניווט ב-Waze
                </a>
              ) : null}
              {meta.maps_url ? (
                <a
                  href={meta.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary w-full inline-flex items-center justify-center"
                >
                  Google Maps
                </a>
              ) : null}
            </div>
          </section>

          <section className="mt-5 surface p-6 text-center space-y-3">
            <h2 className="font-display text-2xl font-bold text-ink">מתנה לזוג</h2>
            <p className="text-[14px] text-ink-mute leading-relaxed">
              אפשר לבחור סכום חופשי ולהעביר ישירות לזוג בביט.
            </p>
            <Link
              to={meta.gift_path}
              className="btn-primary w-full inline-flex items-center justify-center"
            >
              למעבר למתנה
            </Link>
            {!meta.gift_ready ? (
              <p className="text-[12px] text-ink-faint">
                פרטי הביט של הזוג יתווספו בקרוב — עדיין אפשר לרשום מתנה בעמוד.
              </p>
            ) : null}
          </section>

          <p className="mt-10 text-center text-[14px] text-ink-mute leading-relaxed">
            נשמח לחגוג איתכם
            <br />
            <span className="font-display text-xl font-semibold text-ink">{meta.couple_names}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
